// Support picker — full-screen restaurant selector for admin/support agents.
// Shows the last few restaurants opened on this device, "Open another" search, and
// "Create new". A link opens a grid of every restaurant this agent can reach, with
// a filter, for the ones that fall outside the short list.

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOpenRestaurant } from '@/hooks/useOpenRestaurant'
import { RestaurantGrid, roleLabelOf } from './RestaurantGrid'
import { APP_LANDING_PATH } from '@/shell/navRegistry'
import { ArrowLeft, LogOut, Plus, Search, Store, Loader2 } from 'lucide-react'
import { SynvyaMark } from '@synvya/portal-ui/branding'
import { Button } from '@/components/ui/Button'
import { useTeam } from '@/state/useTeam'
import { useAuth } from '@/state/useAuth'
import { useAdminStatus } from '@/state/useAdminStatus'
import { SupportSearch } from './SupportSearch'
import { NewRestaurantSetup } from './NewRestaurantSetup'
import { sortTeams } from '@/lib/recentRestaurants'

/** How many restaurants the first screen lists. The rest are one click away. */
const SHORT_LIST = 4

export function SupportPicker() {
  const navigate = useNavigate()
  const { teams, reset: resetTeam } = useTeam()
  const { logout } = useAuth()
  const { reset: resetAdminStatus } = useAdminStatus()
  const { open: handlePick, opening, error: pickError } = useOpenRestaurant()

  const [view, setView] = useState<'picker' | 'create' | 'all'>('picker')
  const [searchOpen, setSearchOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  // Read once per mount: a pick navigates away, so the order need not update live.
  const ordered = useMemo(() => sortTeams(teams, 'recent'), [teams])

  async function handleLogout() {
    setLoggingOut(true)
    try {
      resetTeam()
      resetAdminStatus()
      await logout()
    } catch {
      setLoggingOut(false)
    }
  }

  function handleSetupComplete() {
    // Navigate without full page reload — a reload would discard
    // the Zustand activeTeamId set by startProvisioning's activateTeam.
    navigate(APP_LANDING_PATH)
  }

  if (view === 'create') {
    return <NewRestaurantSetup onComplete={handleSetupComplete} />
  }

  if (view === 'all') {
    return (
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <button
            type="button"
            onClick={() => setView('picker')}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back
          </button>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            All restaurants <span className="text-sm font-normal text-muted-foreground">({teams.length})</span>
          </h1>
          {pickError && (
            <p className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {pickError}
            </p>
          )}
          <RestaurantGrid teams={teams} opening={opening} onPick={handlePick} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-3">
          <SynvyaMark variant="green" size={48} title="Synvya" />
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Synvya Support</h1>
          <p className="text-center text-sm text-muted-foreground">
            Open an existing restaurant or create a new one
          </p>
        </div>

        {/* Error */}
        {pickError && (
          <p className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            {pickError}
          </p>
        )}

        {/* Recently-opened restaurants */}
        {ordered.length > 0 && (
          <section className="space-y-2">
            <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Recently opened
            </h2>
            <ul className="divide-y divide-border rounded-lg border border-border bg-card">
              {ordered.slice(0, SHORT_LIST).map((team) => {
                const tid = team.id
                const name = team.name
                const isOpening = opening === tid
                const roleLabel = roleLabelOf(team)

                return (
                  <li key={tid}>
                    <button
                      type="button"
                      onClick={() => handlePick(tid)}
                      disabled={opening !== null}
                      className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-accent disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Store className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-medium text-foreground">{name}</span>
                          {roleLabel && (
                            <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
                          )}
                        </div>
                      </div>
                      {isOpening && (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
            {ordered.length > SHORT_LIST && (
              <button
                type="button"
                onClick={() => setView('all')}
                className="px-1 text-sm text-primary underline-offset-2 hover:underline"
              >
                View all {ordered.length} restaurants
              </button>
            )}
          </section>
        )}

        {/* Action buttons — Create first (primary), Search second (outline) */}
        <div className="space-y-2">
          <Button onClick={() => setView('create')} className="w-full justify-start gap-2" disabled={opening !== null}>
            <Plus className="h-4 w-4" aria-hidden />
            Create new restaurant
          </Button>
          <Button onClick={() => setSearchOpen(true)} variant="secondary" className="w-full justify-start gap-2" disabled={opening !== null}>
            <Search className="h-4 w-4" aria-hidden />
            Open another restaurant
          </Button>
        </div>

        {/* Sign out — subtle text link */}
        <div className="text-center">
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-60"
          >
            <LogOut className="h-3 w-3" aria-hidden />
            {loggingOut ? 'Signing out\u2026' : 'Sign out'}
          </button>
        </div>
      </div>

      <SupportSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  )
}
